<template>
  <div v-if="leadJob" data-testid="lead-guard" class="grid gap-2 rounded-xl border-2 border-amber-500/60 bg-amber-500/5 p-4">
    <div class="flex items-start gap-2">
      <TriangleAlert class="h-4 w-4 shrink-0 mt-0.5 text-amber-600 dark:text-amber-500" />
      <div class="grid gap-0.5">
        <p class="text-sm font-medium">{{ t('jobs.leadGuard.title') }}</p>
        <p class="text-xs text-muted-foreground">
          {{ leadJob.attributes.title }}
          <span v-if="leadJob.attributes.reference_number"> · {{ leadJob.attributes.reference_number }}</span>
        </p>
      </div>
    </div>

    <!-- Already branched: state the link instead of re-offering the doors. -->
    <div v-if="branched" class="flex items-center gap-2">
      <p class="text-xs font-medium" data-testid="lead-guard-linked">{{ t('jobs.leadGuard.linked') }}</p>
      <Button type="button" variant="ghost" size="sm" class="h-6 px-2" data-testid="lead-guard-unlink" @click="$emit('unbranch')">
        <X class="h-3.5 w-3.5" />
      </Button>
    </div>
    <div v-else class="flex flex-wrap gap-2">
      <Button type="button" variant="outline" size="sm" data-testid="lead-guard-continue" @click="openJob">
        {{ t('jobs.leadGuard.continue') }}
      </Button>
      <!-- The chain door: the new job records the lead it was opened beside as
           its PARENT, evaluation or not — « created from » is a fact on its
           own. Whether the child is born assessed is the server's reading of
           the parent's look-see, never this button's condition. -->
      <Button
        type="button"
        variant="outline"
        size="sm"
        data-testid="lead-guard-branch"
        @click="$emit('branch', leadJob.id)"
      >
        {{ t('jobs.leadGuard.branch') }}
      </Button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { toRef } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { TriangleAlert, X } from 'lucide-vue-next'

import { Button } from '@/components/ui/button'
import { useLeadGuard } from '@/composables/useLeadGuard'

// The create form's lead guard (both surfaces — /jobs/:id is a uniform path).
// « Continuer » leaves the form for the existing job (the same-scope door);
// « Nouveau job lié » stays in the form and stamps the lead as the new job's
// PARENT (the new-scope door) — the parent form owns that state, so it
// arrives as an event carrying the lead's id.
const props = defineProps<{
  serviceLocationId: string | null
  // Non-null once the parent form stamped an origin — the banner then states
  // the link (with an undo) instead of re-offering the doors.
  branched?: boolean
}>()
const emit = defineEmits<{ branch: [jobId: string]; unbranch: []; navigate: [] }>()

const { t } = useI18n()
const router = useRouter()
const { leadJob } = useLeadGuard(toRef(props, 'serviceLocationId'))

const openJob = () => {
  if (!leadJob.value) return
  emit('navigate')
  // ⚠ Stays on the PATH form deliberately: this banner is shared, and
  // FieldJobCreateForm mounts it on the handheld, whose router has no
  // `lead-show` route — a named push would fail there. /jobs/:id resolves in
  // both apps, and the console's redirect is one-way, so nothing flashes.
  void router.push(`/jobs/${leadJob.value.id}`)
}
</script>
